import { useMemo } from "react";

import { ParameterTimeSeriesChartResult } from "@/lib/api";
import { useSelectedTimeRange } from "@/features/dashboard/store";
import { getChartSeriesPresentationState } from "@/components/graph/chart-series-presentation";
import type { TimeSeriesConfig } from "@/components/graph/graph.types";

type ChartTimeRange = {
  timeStart: Date;
  timeEnd: Date;
};

/**
 * The chart-level "no data" overlay shows exactly when every configured series
 * is in the `noData` / "[not available]" presentation state — the same rule the
 * per-series legend uses, so the overlay and the legend can never disagree.
 *
 * A series is "not available" when it has no usable value in range: missing
 * result, `data === null` (upstream failure), empty datapoints, or every
 * datapoint value `null`. As soon as one series has a real value the overlay is
 * hidden, even if the others are unavailable.
 */
export function isTimeSeriesGroupNoData(
  config: TimeSeriesConfig,
  data: ParameterTimeSeriesChartResult[],
  timeRange: ChartTimeRange,
): boolean {
  return config.series.every((seriesConfig) => {
    const result = data.find((r) => r.parameter.id === seriesConfig.parameter_id);
    return getChartSeriesPresentationState(result, timeRange) === "noData";
  });
}

export function useTimeSeriesNoData(
  config: TimeSeriesConfig,
  data: ParameterTimeSeriesChartResult[],
): boolean {
  const timeRange = useSelectedTimeRange();

  return useMemo(
    () => isTimeSeriesGroupNoData(config, data, timeRange),
    [config.series, data, timeRange],
  );
}
